import { Transaction } from '../types';

const cleanText = (value: unknown): string => String(value ?? '').replace(/\u00A0/g, ' ').trim();
const sanitizeSymbol = (value: unknown): string => cleanText(value).toUpperCase();
const sanitizeCurrency = (value: unknown, fallback = 'GBP'): string => cleanText(value).toUpperCase() || fallback;
const extractIsin = (value: unknown): string => {
    const normalized = cleanText(value).toUpperCase();
    return /^[A-Z]{2}[A-Z0-9]{10}$/.test(normalized) ? normalized : '';
};

const parseLine = (text: string, delimiter = ',') => {
    const result: string[] = [];
    let current = '';
    let inQuote = false;
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (char === '"') inQuote = !inQuote;
        else if (char === delimiter && !inQuote) {
            result.push(current.trim());
            current = '';
        } else current += char;
    }
    result.push(current.trim());
    return result.map((val) => cleanText(val.replace(/^"|"$/g, '')));
};

const detectDelimiter = (lines: string[]): string => {
    const sample = lines.slice(0, 5);
    const delimiters = [',', '\t', ';'];
    let best = ',';
    let bestScore = -1;
    for (const delimiter of delimiters) {
        const counts = sample.map((line) => parseLine(line, delimiter).length).filter((count) => count > 1);
        if (!counts.length) continue;
        const average = counts.reduce((sum, count) => sum + count, 0) / counts.length;
        const consistency = counts.filter((count) => count === counts[0]).length / counts.length;
        const score = average + consistency;
        if (score > bestScore) {
            best = delimiter;
            bestScore = score;
        }
    }
    return best;
};

const getOptionalNum = (cols: string[], idx: number): number | null => {
    if (idx < 0 || !cols[idx]) return null;
    const cleanStr = cols[idx].replace(/,/g, '').replace(/[£$€]/g, '');
    const val = parseFloat(cleanStr);
    return Number.isFinite(val) ? val : null;
};

const mapTrading212Action = (actionRaw: string): Transaction['action'] | null => {
    const action = actionRaw.trim().toLowerCase();
    if (action === 'market buy' || action === 'buy') return 'Buy';
    if (action === 'market sell' || action === 'sell') return 'Sell';
    if (action === 'dividend' || action === 'dividend (dividend)' || action === 'interest') return 'Dividend';
    if (action === 'fee' || action === 'withdrawal' || action === 'cash withdrawal') return 'Withdrawal';
    if (action === 'deposit') return 'Deposit';
    if (action.includes('buy')) return 'Buy';
    if (action.includes('sell')) return 'Sell';
    if (action.includes('dividend') || action.includes('interest')) return 'Dividend';
    if (action.includes('deposit')) return 'Deposit';
    if (action.includes('withdrawal') || action.includes('fee')) return 'Withdrawal';
    return null;
};

const parseDate = (dateStr: string): Date | null => {
    try {
        const firstPart = dateStr.split(/[-/]/)[0];
        const dateObj =
            firstPart.length === 4
                ? new Date(dateStr.replace(' ', 'T'))
                : (() => {
                      const normalizedDate = dateStr.replace(/-/g, '/');
                      const [datePart, timePart] = normalizedDate.split(' ');
                      const [day, month, year] = datePart.split('/');
                      if (!day || !month || !year || Number.isNaN(parseInt(day))) return null;
                      return new Date(`${year}-${month}-${day}T${timePart || '00:00:00'}`);
                  })();
        return dateObj && !Number.isNaN(dateObj.getTime()) ? dateObj : null;
    } catch {
        return null;
    }
};

export const parseTrading212Csv = (csvText: string): Transaction[] => {
    const lines = csvText.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) return [];

    const delimiter = detectDelimiter(lines);
    const headerLineIndex = lines.findIndex((line) => {
        const cols = parseLine(line, delimiter).map((h) => h.toLowerCase());
        return cols.includes('action') && (cols.includes('time') || cols.includes('date'));
    });
    if (headerLineIndex < 0 || headerLineIndex >= lines.length - 1) return [];

    const headerCols = parseLine(lines[headerLineIndex], delimiter).map((h) => h.toLowerCase());

    const idx = (h: string) => headerCols.indexOf(h);
    const idxAction = idx('action');
    const idxTime = idx('time');
    const idxTicker = idx('ticker');
    const idxName = idx('name');
    const idxId = idx('id');
    const idxIsin = idx('isin');
    const idxExchange = idx('exchange');
    const idxQty = idx('no. of shares');
    const idxPrice = idx('price / share');
    const idxPriceCurrency = idx('currency (price / share)');
    const idxExchangeRate = idx('exchange rate');
    const idxTotal = idx('total');
    const idxResult = idx('result');
    const idxTotalCurrency = idx('currency (total)');
    const idxResultCurrency = idx('currency (result)');

    const safeIdxTotal = idxTotal !== -1 ? idxTotal : idxResult;
    const safeIdxTotalCurrency = idxTotalCurrency !== -1 ? idxTotalCurrency : idxResultCurrency;

    const parsedTx: Transaction[] = [];
    for (let i = headerLineIndex + 1; i < lines.length; i++) {
        const cols = parseLine(lines[i], delimiter);
        const actionRaw = cleanText(idxAction >= 0 ? cols[idxAction] : cols[0]);
        const mappedAction = mapTrading212Action(actionRaw);
        if (!mappedAction) continue;

        const dateObj = parseDate(cleanText(idxTime >= 0 ? cols[idxTime] : cols[1]));
        if (!dateObj) continue;

        const symbolRaw = idxTicker >= 0 ? cols[idxTicker] : '';
        const actionLower = actionRaw.toLowerCase();
        let symbol = sanitizeSymbol(symbolRaw);
        const name = cleanText(idxName >= 0 ? cols[idxName] : '') || symbol;
        if (!symbol && (actionLower.includes('interest') || actionLower.includes('deposit') || actionLower.includes('withdrawal') || actionLower.includes('cash'))) {
            symbol = 'CASH';
        }
        if (!symbol && name) symbol = sanitizeSymbol(name);
        if (!symbol) continue;

        const brokerTxId = cleanText(idxId >= 0 ? cols[idxId] : '');
        const id = brokerTxId || `GEN_${`${dateObj.getTime()}_${actionRaw}_${symbol}_${i}`.replace(/[^a-zA-Z0-9_]/g, '')}`;

        const quantity = Math.abs(getOptionalNum(cols, idxQty) ?? 0);
        const pricePerShare = getOptionalNum(cols, idxPrice);
        const total = getOptionalNum(cols, safeIdxTotal) ?? getOptionalNum(cols, idxResult);
        const fxRate = getOptionalNum(cols, idxExchangeRate) ?? undefined;

        const totalCurrency = sanitizeCurrency(
            (safeIdxTotalCurrency >= 0 ? cols[safeIdxTotalCurrency] : '') || (idxResultCurrency >= 0 ? cols[idxResultCurrency] : 'GBP'),
            'GBP'
        );
        const priceCurrency = sanitizeCurrency(idxPriceCurrency >= 0 ? cols[idxPriceCurrency] : '', totalCurrency);

        let unitPrice = Math.abs(pricePerShare ?? 0);
        if ((!unitPrice || unitPrice <= 0) && quantity > 0 && Number.isFinite(total ?? NaN)) {
            unitPrice = Math.abs((total as number) / quantity);
        }

        const unresolvedAmount = (mappedAction === 'Buy' || mappedAction === 'Sell') && (!quantity || (!unitPrice && !total));

        parsedTx.push({
            id,
            brokerTxId: brokerTxId || undefined,
            date: dateObj.toISOString(),
            action: mappedAction,
            symbol,
            displaySymbol: symbol,
            name,
            isin: extractIsin(idxIsin >= 0 ? cols[idxIsin] : '') || undefined,
            exchange: cleanText(idxExchange >= 0 ? cols[idxExchange] : '').toUpperCase() || undefined,
            quantity,
            shares: quantity || undefined,
            price: unitPrice,
            pricePerShare: pricePerShare ?? undefined,
            originalPricePerShare: pricePerShare ?? undefined,
            originalPriceCurrency: (idxPriceCurrency >= 0 ? sanitizeCurrency(cols[idxPriceCurrency], '') : '') || undefined,
            originalExchangeRate: fxRate,
            priceCurrency,
            amount: Math.abs(total ?? quantity * unitPrice),
            total: total ?? undefined,
            currency: totalCurrency,
            totalCurrency,
            fxRate,
            unresolvedAmount: unresolvedAmount || undefined,
            unresolved: unresolvedAmount || undefined,
            source: 'csv',
            provider: 'trading212',
            sourceLabel: 'Trading 212 CSV',
        });
    }

    return parsedTx;
};
